import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * CategoryMultiSelect Component
 * 
 * Multi-select dropdown for selecting multiple categories
 */
const CategoryMultiSelect = ({ 
  categories = [], 
  selectedCategoryIds = [], 
  onSelectionChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleCategory = (categoryId) => {
    const categoryIdStr = String(categoryId).trim();
    const newSelection = selectedCategoryIds.some(id => String(id).trim() === categoryIdStr)
      ? selectedCategoryIds.filter(id => String(id).trim() !== categoryIdStr)
      : [...selectedCategoryIds, categoryId];
    
    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    const allCategoryIds = categories.map(cat => cat._id);
    if (selectedCategoryIds.length === categories.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(allCategoryIds);
    }
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleRemovePill = (categoryId, e) => {
    e.stopPropagation();
    const categoryIdStr = String(categoryId).trim();
    const newSelection = selectedCategoryIds.filter(id => String(id).trim() !== categoryIdStr);
    onSelectionChange(newSelection);
  };

  const selectedCategories = categories.filter(cat => 
    selectedCategoryIds.some(id => String(id).trim() === String(cat._id).trim())
  );

  const getDisplayText = () => {
    if (selectedCategoryIds.length === 0) {
      return 'All Categories';
    }
    if (selectedCategoryIds.length === 1) {
      return selectedCategories[0]?.name || '1 category';
    }
    return `${selectedCategoryIds.length} categories`;
  };

  const safeCategories = Array.isArray(categories) ? categories : [];

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
      <div className="relative">
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 focus:ring-purple-500 focus:border-purple-500 transition-colors min-h-[2.5rem]"
        >
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            {selectedCategoryIds.length === 0 ? (
              <span className="text-gray-500">All Categories</span>
            ) : (
              selectedCategories.map(category => (
                <span
                  key={category._id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                >
                  {category.name}
                  <button
                    type="button"
                    onClick={(e) => handleRemovePill(category._id, e)}
                    className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))
            )}
          </div>
          <ChevronDownIcon 
            className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-64 overflow-y-auto">
            <div className="p-2">
              {/* Header Actions */}
              <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-200 mb-1">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  {selectedCategoryIds.length === safeCategories.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedCategoryIds.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Category List */}
              <div className="py-1">
                {safeCategories.length === 0 ? (
                  <div className="px-2 py-4 text-center text-xs text-gray-500">
                    No categories available
                  </div>
                ) : (
                  safeCategories.map((category) => {
                    const isSelected = selectedCategoryIds.some(id => 
                      String(id).trim() === String(category._id).trim()
                    );
                    
                    return (
                      <button
                        key={category._id}
                        type="button"
                        onClick={() => handleToggleCategory(category._id)}
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-xs text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className={`flex-shrink-0 w-4 h-4 border-2 rounded flex items-center justify-center ${
                          isSelected 
                            ? 'bg-purple-600 border-purple-600' 
                            : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <CheckIcon className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span className={`flex-1 ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {category.name}
                        </span>
                        {category.color && (
                          <span 
                            className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                            style={{ backgroundColor: category.color }}
                            title={category.color}
                          />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryMultiSelect;
